'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { PageFrame } from '../ui'
import { baseDashboardClasses } from '../BaseDashboard'
import { apiGet, apiPost, useCatalog } from './entriesApi'
import { SearchableSelect } from './SearchableSelect'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { TokenChainGraph, type ChainRoute, type ChainTransition } from './TokenChainGraph'

// Administration › Token Chain. The sourcing engine's warm_token_chain
// strategy is a Markov chain over the stems phishing names are built from
// (jupyter-fraud libs/tooling/domain_token_chain.py). This page is how you
// look inside it: what it learned from a platform's confirmed hostnames, the
// queries it will ask in rank order with the numbers behind each rank, and
// -- the reason the page exists -- why a given hostname is or is not reached.
//
// The model lives in Python, so the page asks a worker for it through the
// jobs board (auto_domains.explain_chain) and reads the result off the run,
// the same way the vocabulary preview does. Nothing here searches or writes.

type Stem = { stem: string; count: number; members: string[]; is_head: boolean; is_lure: boolean }
type Transition = { a: string; b: string; count: number; sep: string; examples: string[] }
type Query = {
  rank: number; query: string; how: string; parts: string[]; p_path: number; p_page: number
  score: number; known_total: number | null; examples: string[]; runs: boolean
}
type TraceToken = { part: string; stem: string | null; sep: string; seen: number; role: 'brand' | 'lure' | null }
type TraceHit = { rank: number; query: string; how: string; prob: number; coverage: number; score: number; known_total: number | null; runs: boolean }
type Trace = { host: string; name: string; tokens: TraceToken[]; hits: TraceHit[]; status: string; verdict: string }
type Explain = {
  empty?: boolean; reason?: string; held_out?: string[]
  model: { names: number; stems: number; transitions: number; paths3: number; heads: string[]; mu: number; lure_pseudo: number; candidates: number; budget: number; page_rows: number; known_totals: number }
  stems: Stem[]; transitions: Transition[]; queries: Query[]; trace?: Trace
  /** Every name's stem walk (newer workers only) — host routes and every host behind a link. */
  routes?: ChainRoute[]
}
type JobRunRow = { id: string; status: string; result?: Explain | null; error?: string | null }

const HOW_LABEL: Record<string, string> = {
  observed: 'seen in a name', 'observed-3': 'seen, 3 stems', backoff: 'inferred', 'backoff-pinned': 'inferred, pinned', family: 'word family',
}
// status is said in words and an icon, never by colour alone
const VERDICT: Record<string, { icon: string; label: string; tone: string }> = {
  reached: { icon: '✓', label: 'Reached', tone: 'text-success border-success/40 bg-emerald-500/5' },
  luck: { icon: '≈', label: 'Only by luck', tone: 'text-warning border-warning-border bg-warning-surface/60' },
  beyond_budget: { icon: '…', label: 'Beyond the budget', tone: 'text-warning border-warning-border bg-warning-surface/60' },
  no_brand: { icon: '✕', label: 'Not reached', tone: 'text-destructive border-destructive/40 bg-danger-surface' },
  unknown_stem: { icon: '✕', label: 'Not reached', tone: 'text-destructive border-destructive/40 bg-danger-surface' },
  too_broad: { icon: '✕', label: 'Not reached', tone: 'text-destructive border-destructive/40 bg-danger-surface' },
}

const pct = (v: number) => `${Math.round(v * 100)}%`

export function TokenChainView({ theme }: { theme: ProcessedTheme }) {
  const { isDark, border, panelBg, softBg, label, muted, text } = baseDashboardClasses(theme)
  const { catalog, loading: catalogLoading } = useCatalog()
  const [platformId, setPlatformId] = useState<number | ''>('')
  const [data, setData] = useState<Explain | null>(null)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const [host, setHost] = useState('')
  const [holdOut, setHoldOut] = useState(true)
  const [selected, setSelected] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const [showAll, setShowAll] = useState(false)
  // the graph: how sparse, which walk is lit, which link is open, the overlay
  const [minCount, setMinCount] = useState(2)
  const [highlight, setHighlight] = useState<{ label: string; stems: string[] } | null>(null)
  const [activeEdge, setActiveEdge] = useState<ChainTransition | null>(null)
  const [fullOpen, setFullOpen] = useState(false)
  const [fullMin, setFullMin] = useState(1)
  const [fullSearch, setFullSearch] = useState('')

  useEffect(() => {
    try {
      const q = new URLSearchParams(window.location.search)
      const id = Number(q.get('platform_id'))
      if (Number.isFinite(id) && id > 0) setPlatformId(id)
      const h = q.get('host'); if (h) setHost(h)
    } catch { /* SSR */ }
  }, [])

  // The model is built by a worker: queue the run, poll it.
  const run = useCallback(async (pid: number, traceHost?: string, without?: boolean) => {
    setBusy(true); setNote('asking a worker…')
    const queued = await apiPost<{ id: string }>('/auto-domains/chain/explain', {
      platform_id: pid, ...(traceHost ? { host: traceHost, ...(without ? { without: [traceHost] } : {}) } : {}),
    })
    if (!queued.success || !queued.data?.id) {
      setBusy(false); setNote(typeof queued.error === 'string' ? queued.error : 'Could not queue the request'); return
    }
    for (let i = 0; i < 45; i++) {
      await new Promise((r) => setTimeout(r, 1500))
      const res = await apiGet<JobRunRow>(`/auto-domains/brands/preview/${queued.data.id}`)
      const row = res.data
      if (!res.success || !row) continue
      if (row.status === 'succeeded' && row.result) { setData(row.result); setNote(null); setBusy(false); return }
      if (row.status === 'failed' || row.status === 'cancelled') { setNote(`${row.status}: ${(row.error ?? '').slice(0, 220)}`); setBusy(false); return }
      setNote(row.status === 'queued' ? 'waiting for a worker…' : 'building the chain…')
    }
    setBusy(false)
    setNote('no worker answered — is an auto-domains worker online? (Operations › Jobs)')
  }, [])

  useEffect(() => { if (platformId !== '') { setData(null); setSelected(null); void run(platformId) } }, [platformId, run])

  // a trace lights the traced host's own walk
  useEffect(() => {
    setActiveEdge(null)
    const t = data?.trace
    setHighlight(t ? { label: t.host, stems: t.tokens.map((k) => k.stem).filter((x): x is string => !!x) } : null)
  }, [data])

  const routes = data?.routes
  const routeOf = (h: string) => routes?.find((r) => r.host === h)?.stems ?? null
  const edgeHosts = useMemo(() => {
    if (!routes || !activeEdge) return []
    return routes.filter((r) => r.stems.some((s, i) => s === activeEdge.a && r.stems[i + 1] === activeEdge.b)).map((r) => r.host).sort()
  }, [routes, activeEdge])
  const stemRoutes = useMemo(() => (routes && selected ? routes.filter((r) => r.stems.includes(selected)) : []), [routes, selected])
  const lightHost = (h: string) => { const st = routeOf(h); if (st) setHighlight({ label: h, stems: st }) }

  const platforms = useMemo(() => [...catalog.platforms].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')), [catalog.platforms])
  const stem = data?.stems.find((s) => s.stem === selected) ?? null
  const queries = useMemo(() => {
    if (!data) return []
    const f = filter.trim().toLowerCase()
    return data.queries.filter((q) => (!selected || q.parts.includes(selected)) && (!f || q.query.includes(f) || q.how.includes(f)))
  }, [data, filter, selected])
  const shown = showAll ? queries : queries.slice(0, 80)
  const maxPath = Math.max(...(data?.queries.map((q) => q.p_path) ?? [0.0001]))
  const card = `rounded-sm border ${border} ${panelBg}`
  const h2 = `font-mono text-[10px] uppercase tracking-widest ${muted}`
  const input = `h-8 rounded border bg-transparent px-2 font-mono text-[11px] ${border} ${text} outline-none focus:border-signal`
  const trace = data?.trace
  const v = trace ? VERDICT[trace.status] ?? VERDICT.too_broad : null

  return (
    <PageFrame padded={false} eyebrow="Administration · Token Chain" title="Token Chain"
      subtitle={<>How sourcing turns the phishing names we already know into the searches it runs next. Names are split into stems
          (<span className="font-mono">ctt · desalfandeg</span>), the chain counts which stem follows which, and each possible
          search is ranked by how likely the path is × how likely its results fit on one page.</>}>
    <div className="p-4">
      <div className={`mb-4 flex flex-wrap items-center gap-3 px-4 py-3 ${card}`}>
        <span className={h2}>Platform</span>
        <div className={`w-64 ${catalogLoading ? 'pointer-events-none opacity-60' : ''}`}>
          <SearchableSelect theme={theme} value={platformId === '' ? undefined : platformId} clearable={false} options={platforms}
            placeholder="Pick a platform…" searchPlaceholder="Search platforms…"
            onChange={(id) => setPlatformId(id ? Number(id) : '')} />
        </div>
        {platformId !== '' && (
          <button type="button" disabled={busy} onClick={() => void run(platformId)} className={`h-8 rounded border px-3 font-mono text-[10px] uppercase tracking-widest ${border} ${muted} hover:text-signal disabled:opacity-50`}>
            {busy ? 'Working…' : 'Rebuild'}
          </button>
        )}
        {note && <span className={`font-mono text-[11px] ${muted}`}>{note}</span>}
        {data && !data.empty && (
          <span className={`ml-auto font-mono text-[11px] ${muted}`}>
            {data.model.names} names · {data.model.stems} stems · {data.model.transitions} links · {data.model.paths3} three-stem paths · {data.model.candidates} possible searches, {data.model.budget} run
          </span>
        )}
      </div>

      {platformId === '' && <div className={`px-4 py-10 text-center font-mono text-[11px] ${card} ${muted}`}>Pick a platform to see its chain.</div>}
      {data?.empty && <div className={`px-4 py-10 text-center font-mono text-[11px] ${card} ${muted}`}>{data.reason}</div>}

      {data && !data.empty && (
        <>
          {/* -- tracer -------------------------------------------------- */}
          <div className={`mb-4 p-4 ${card}`}>
            <div className={h2}>Why was a host found — or missed?</div>
            <form className="mt-2 flex flex-wrap items-center gap-3" onSubmit={(e) => { e.preventDefault(); if (host.trim() && platformId !== '') void run(platformId, host.trim().toLowerCase(), holdOut) }}>
              <input value={host} onChange={(e) => setHost(e.target.value)} placeholder="ctt-desalfandegamento.com" className={`${input} w-80`} />
              <button type="submit" disabled={busy || !host.trim()} className="h-8 rounded-sm bg-primary text-primary-foreground px-3 text-[11px] font-medium text-white hover:bg-signal disabled:opacity-40">Trace</button>
              <label className={`flex items-center gap-1.5 font-mono text-[11px] ${muted}`} title="Rebuild the chain without this host first: would we have found it had nobody reported it?">
                <input type="checkbox" checked={holdOut} onChange={(e) => setHoldOut(e.target.checked)} /> as if it had never been reported
              </label>
            </form>
            {trace && v && (
              <div className="mt-3">
                <div className="flex flex-wrap items-center gap-1.5">
                  {trace.tokens.map((t, i) => (
                    <span key={i} className="flex items-center gap-1.5">
                      <span className={`rounded-sm border px-2 py-1 font-mono text-[11px] ${border} ${t.stem ? text : muted} ${t.stem ? softBg : ''}`}
                        title={t.stem ? `${t.part} → stem ${t.stem} · in ${t.seen} known name(s)${t.role ? ` · ${t.role}` : ''}` : 'ignored: digits, a connector, or under 3 letters'}>
                        {t.stem && t.stem !== t.part ? <><span className={muted}>{t.part} → </span>{t.stem}</> : t.part}
                        {t.role === 'brand' && <span className="ml-1.5 text-signal">brand</span>}
                        {t.stem && !t.role && t.seen === 0 && <span className="ml-1.5 text-destructive">unknown</span>}
                        {t.stem && t.role !== 'brand' && (t.seen > 0 || t.role === 'lure') && <span className={`ml-1.5 ${muted}`}>{t.seen > 0 ? `${t.seen}×` : 'lexicon'}</span>}
                      </span>
                      {t.sep && <span className={`font-mono text-[11px] ${muted}`}>{t.sep}</span>}
                    </span>
                  ))}
                </div>
                <div className={`mt-2 rounded-sm border px-3 py-2 text-[12px] ${v.tone}`}>
                  <span className="font-semibold">{v.icon} {v.label}.</span> <span className={text}>{trace.verdict}</span>
                  {data.held_out && data.held_out.length > 0 && <span className={muted}> (chain rebuilt without {data.held_out.join(', ')})</span>}
                </div>
                {trace.hits.length > 0 && (
                  <table className="mt-2 w-full text-[11px]">
                    <tbody>
                      {trace.hits.map((h) => (
                        <tr key={h.query} className={'border-b border-border'}>
                          <td className={`w-14 py-1 font-mono ${h.runs ? text : muted}`}>#{h.rank}</td>
                          <td className={`py-1 font-mono ${text}`}>{h.query}</td>
                          <td className={`py-1 ${muted}`}>{HOW_LABEL[h.how] ?? h.how}</td>
                          <td className={`py-1 font-mono ${muted}`}>{h.known_total != null ? `${h.known_total} results (recorded)` : `fits a page ${pct(h.coverage)}`}</td>
                          <td className={`py-1 text-right font-mono ${h.runs ? 'text-success' : muted}`}>{h.runs ? 'runs' : 'beyond budget'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>

          {/* -- graph ---------------------------------------------------- */}
          <div className={`mb-4 p-4 ${card}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className={h2}>What the chain learned — {selected ? <>two steps around “{selected}”</> : 'two steps around the brand'}</div>
              <div className="flex flex-wrap items-center gap-3">
                <label className={`flex items-center gap-2 font-mono text-[10px] ${muted}`}>
                  seen at least
                  <input type="range" min={1} max={10} value={minCount} onChange={(e) => setMinCount(Number(e.target.value))} />
                  <span className={text}>{minCount}×</span>
                </label>
                <button type="button" onClick={() => setFullOpen(true)} className={`h-7 rounded border px-3 font-mono text-[10px] uppercase tracking-widest ${border} ${muted} hover:text-sky-500`}>
                  See the whole chain
                </button>
              </div>
            </div>
            <div className={`mt-1 font-mono text-[10px] ${muted}`}>
              columns = where a stem usually sits relative to the brand · line thickness = names with that link · number = names with the stem · names sharing a stem cross there · click a stem to centre on it, a link to list its names
            </div>
            {!routes && (
              <div className="mt-1 font-mono text-[10px] text-amber-500">
                This worker predates host routes: links and stems are complete, but a host’s whole path and the full name list per link need the jobs worker restarted.
              </div>
            )}
            {highlight && (
              <div className={`mt-2 flex flex-wrap items-center gap-2 font-mono text-[11px] ${muted}`}>
                <span className="text-amber-500">lit:</span>
                <span className={text}>{highlight.label}</span>
                <span>{highlight.stems.join(' → ')}</span>
                <button type="button" onClick={() => setHighlight(null)} className="uppercase tracking-widest hover:text-sky-500">clear</button>
              </div>
            )}
            <div className="mt-2 overflow-x-auto">
              <TokenChainGraph
                stems={data.stems} transitions={data.transitions} routes={routes} heads={data.model.heads}
                isDark={isDark} mode="neighbourhood" minCount={minCount}
                selected={selected} onSelect={(st) => { setSelected(st); setActiveEdge(null) }}
                highlight={highlight?.stems} activeEdge={activeEdge} onEdge={setActiveEdge}
              />
            </div>
            {activeEdge && (
              <div className={`mt-3 rounded-sm border px-3 py-2 text-[11px] ${border} ${softBg}`}>
                <span className={`font-mono font-semibold ${text}`}>{activeEdge.a}{activeEdge.sep || '·'}{activeEdge.b}</span>
                <span className={muted}> · in {activeEdge.count} name(s)</span>
                <button type="button" onClick={() => setActiveEdge(null)} className={`ml-3 font-mono text-[10px] uppercase tracking-widest ${muted} hover:text-sky-500`}>close</button>
                <HostList hosts={routes ? edgeHosts : activeEdge.examples} partial={!routes} onLight={lightHost} onTrace={(h) => { setHost(h); if (platformId !== '') void run(platformId, h, holdOut) }} muted={muted} text={text} canLight={!!routes} />
              </div>
            )}
            {stem && (
              <div className={`mt-3 rounded-sm border px-3 py-2 text-[11px] ${border} ${softBg}`}>
                <span className={`font-mono font-semibold ${text}`}>{stem.stem}</span>
                <span className={muted}> · in {stem.count} name(s){stem.is_head ? ' · brand' : ''}{stem.is_lure ? ' · in the lure lexicon' : ''} · words: </span>
                <span className={`font-mono ${text}`}>{stem.members.join(', ') || stem.stem}</span>
                <button type="button" onClick={() => setSelected(null)} className={`ml-3 font-mono text-[10px] uppercase tracking-widest ${muted} hover:text-signal`}>clear</button>
                {routes && stemRoutes.length > 0 && (
                  <div className="mt-2">
                    <div className={`font-mono text-[10px] uppercase tracking-widest ${muted}`}>names that pass through it — click one to light its path</div>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {stemRoutes.slice(0, 120).map((r) => (
                        <button key={r.host} type="button" onClick={() => setHighlight({ label: r.host, stems: r.stems })}
                          className={`rounded-sm border px-1.5 py-0.5 font-mono text-[10px] ${border} ${highlight?.label === r.host ? 'border-warning text-warning' : text} hover:border-warning`}
                          title={r.stems.join(' → ')}>
                          {r.host}
                        </button>
                      ))}
                      {stemRoutes.length > 120 && <span className={`font-mono text-[10px] ${muted}`}>+{stemRoutes.length - 120} more</span>}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* -- queries ------------------------------------------------- */}
          <div className={`p-4 ${card}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className={h2}>The searches, in the order they are worth asking{selected ? ` — with “${selected}”` : ''}</div>
              <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="filter…" className={`${input} w-48`} />
            </div>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full min-w-[760px] text-[11px]">
                <thead>
                  <tr className={`text-left font-mono text-[9px] uppercase tracking-widest ${label}`}>
                    <th className="w-12 py-1.5">#</th><th>Search</th><th>Why</th>
                    <th className="w-40" title="How probable this path of stems is, given the names we know">Path likelihood</th>
                    <th className="w-44" title={`Chance the result set fits on one page of ${data.model.page_rows}; MerkleMap's real total when a past run recorded one`}>Fits a page</th>
                    <th className="w-16 text-right">Runs</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((q, i) => (
                    <tr key={q.query} title={q.examples.length ? `seen in: ${q.examples.join(', ')} · click to light its path` : 'not seen in a name: inferred · click to light its path'}
                      onClick={() => setHighlight(highlight?.label === q.query ? null : { label: q.query, stems: q.parts })}
                      className={`cursor-pointer ${highlight?.label === q.query ? 'bg-warning-surface' : ''} border-t border-border ${!q.runs ? 'opacity-60' : ''} ${i > 0 && shown[i - 1].runs && !q.runs ? 'border-t-2 border-t-warning-border' : ''}`}>
                      <td className={`py-1 font-mono ${muted}`}>{q.rank}</td>
                      <td className={`py-1 font-mono ${text}`}>{q.query}</td>
                      <td className={`py-1 ${muted}`}>{HOW_LABEL[q.how] ?? q.how}</td>
                      <td className="py-1 pr-3">
                        <div className="flex items-center gap-2">
                          <div className={`h-1.5 flex-1 rounded-full bg-secondary`}><div className="h-1.5 rounded-full bg-signal" style={{ width: `${Math.max(2, (q.p_path / maxPath) * 100)}%` }} /></div>
                          <span className={`w-12 text-right font-mono ${muted}`}>{(q.p_path * 100).toFixed(2)}%</span>
                        </div>
                      </td>
                      <td className="py-1 pr-3">
                        {q.known_total != null ? (
                          <span className={`font-mono ${q.known_total <= data.model.page_rows ? 'text-success' : 'text-warning'}`}>
                            {q.known_total <= data.model.page_rows ? '✓' : '⚠'} {q.known_total} results <span className={muted}>(recorded)</span>
                          </span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className={`h-1.5 flex-1 rounded-full bg-secondary`}><div className="h-1.5 rounded-full bg-sky-500/60" style={{ width: pct(q.p_page) }} /></div>
                            <span className={`w-12 text-right font-mono ${muted}`}>{pct(q.p_page)}</span>
                          </div>
                        )}
                      </td>
                      <td className={`py-1 text-right font-mono ${q.runs ? 'text-success' : muted}`}>{q.runs ? 'yes' : 'no'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={`mt-2 flex flex-wrap items-center gap-3 font-mono text-[10px] ${muted}`}>
              <span>the first {data.model.budget} run each sweep · {data.model.candidates} possible · {data.model.known_totals} recorded totals feed the ranking</span>
              {queries.length > shown.length && <button type="button" onClick={() => setShowAll(true)} className="uppercase tracking-widest hover:text-signal">show all {queries.length}</button>}
            </div>
          </div>
        </>
      )}

      {fullOpen && data && !data.empty && (
        <div className={`fixed inset-0 z-[80] flex flex-col ${isDark ? 'bg-[#080d12]' : 'bg-white'}`}>
          <div className={`flex flex-wrap items-center gap-3 border-b px-4 py-3 ${border}`}>
            <span className={h2}>The whole chain · {data.stems.length} stems · {data.transitions.length} links</span>
            <input value={fullSearch} onChange={(e) => setFullSearch(e.target.value)} placeholder="find a stem…" className={`${input} w-48`} />
            <label className={`flex items-center gap-2 font-mono text-[10px] ${muted}`}>
              seen at least
              <input type="range" min={1} max={10} value={fullMin} onChange={(e) => setFullMin(Number(e.target.value))} />
              <span className={text}>{fullMin}×</span>
            </label>
            <span className={`font-mono text-[10px] ${muted}`}>drag to pan · wheel to zoom · click a link or stem</span>
            {highlight && <span className="font-mono text-[11px] text-amber-500">lit: {highlight.label}</span>}
            <button type="button" onClick={() => setFullOpen(false)} className={`ml-auto h-7 rounded border px-3 font-mono text-[10px] uppercase tracking-widest ${border} ${muted} hover:text-sky-500`}>Close</button>
          </div>
          <div className="relative min-h-0 flex-1">
            <TokenChainGraph
              stems={data.stems} transitions={data.transitions} routes={routes} heads={data.model.heads}
              isDark={isDark} mode="full" minCount={fullMin} search={fullSearch}
              selected={selected} onSelect={(st) => { setSelected(st); setActiveEdge(null) }}
              highlight={highlight?.stems} activeEdge={activeEdge} onEdge={setActiveEdge}
            />
            {activeEdge && (
              <div className={`absolute bottom-4 left-4 max-h-[40%] max-w-xl overflow-auto rounded-sm border px-3 py-2 text-[11px] shadow-xl ${border} ${panelBg}`}>
                <span className={`font-mono font-semibold ${text}`}>{activeEdge.a}{activeEdge.sep || '·'}{activeEdge.b}</span>
                <span className={muted}> · in {activeEdge.count} name(s)</span>
                <button type="button" onClick={() => setActiveEdge(null)} className={`ml-3 font-mono text-[10px] uppercase tracking-widest ${muted} hover:text-sky-500`}>close</button>
                <HostList hosts={routes ? edgeHosts : activeEdge.examples} partial={!routes} onLight={lightHost} muted={muted} text={text} canLight={!!routes} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    </PageFrame>
  )
}

// Every name behind a link (or, on an older worker, the 3 examples it sends).
function HostList({ hosts, partial, onLight, onTrace, muted, text, canLight }: {
  hosts: string[]; partial: boolean; onLight: (h: string) => void; onTrace?: (h: string) => void; muted: string; text: string; canLight: boolean
}) {
  return (
    <div className="mt-2">
      <div className={`font-mono text-[10px] uppercase tracking-widest ${muted}`}>
        {partial ? 'examples (this worker sends 3)' : `all ${hosts.length} name(s)`}{canLight ? ' — click to light its path' : ''}
      </div>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {hosts.map((h) => (
          <span key={h} className="flex items-center gap-1">
            <button type="button" disabled={!canLight} onClick={() => onLight(h)} className={`rounded-sm border border-slate-500/30 px-1.5 py-0.5 font-mono text-[10px] ${text} enabled:hover:border-amber-500`}>{h}</button>
            {onTrace && <button type="button" onClick={() => onTrace(h)} title="Trace this host" className={`font-mono text-[9px] uppercase ${muted} hover:text-sky-500`}>trace</button>}
          </span>
        ))}
      </div>
    </div>
  )
}
